import React, { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useData } from '../../contexts/DataContext';
import { isToolsFirst } from '../../utils/onboardingMode';
import AppShell from './AppShell';
import { Sidebar, SidebarSection, SidebarItem } from './Sidebar';
import { ReactComponent as DashboardIcon } from '../../assets/svg/sidebar/dashboard.svg';
import { ReactComponent as DashboardIconSelected } from '../../assets/svg/sidebar/dashboard-selected.svg';
import { ReactComponent as ChatIcon } from '../../assets/svg/sidebar/chat.svg';
import { ReactComponent as ChatIconSelected } from '../../assets/svg/sidebar/chat-selected.svg';
import { ReactComponent as CalendarIcon } from '../../assets/svg/sidebar/calendar.svg';
import { ReactComponent as CalendarIconSelected } from '../../assets/svg/sidebar/calendar-selected.svg';
import { ReactComponent as DocumentsIcon } from '../../assets/svg/sidebar/documents.svg';
import { ReactComponent as DocumentsIconSelected } from '../../assets/svg/sidebar/documents-selected.svg';
import { ReactComponent as ProgressIcon } from '../../assets/svg/sidebar/progress.svg';
import { ReactComponent as ProgressIconSelected } from '../../assets/svg/sidebar/progress-selected.svg';
import { ReactComponent as PersonIcon } from '../../assets/svg/person-head.svg';
import { ReactComponent as SettingsIcon } from '../../assets/svg/sidebar/settings.svg';
import { STUDY_GUIDE_ENABLED } from '../../config/featureFlags';
import ThemeLoader from '../ThemeLoader';

/**
 * AppLayout wraps authenticated app routes with AppShell and sidebar only.
 * This layout should be wrapped with RequireAuth/ProtectedRoute at the routing level.
 */
// Inline icons for admin sidebar entries (small, theme-aware via currentColor)
const ClassesAdminIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 10 12 5 2 10l10 5 10-5Z" />
    <path d="M6 12v5c0 1.2 2.7 3 6 3s6-1.8 6-3v-5" />
  </svg>
);
const OrgAdminIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 21V9h6v12M3 9h18M9 6h.01M15 6h.01" />
  </svg>
);

function AppLayout() {
  const { accessToken, user, hasAnyOrgAdminRole, getAdminOrgIds } = useAuth();
  const { preferences } = usePreferences();
  const { courses } = useData();
  const location = useLocation();
  const currentPath = location.pathname;
  const isAdmin = user?.roles?.some(role => role.roleName === "admin");
  const showAdminNav = isAdmin || (typeof hasAnyOrgAdminRole === 'function' && hasAnyOrgAdminRole());
  const adminOrgIds = typeof getAdminOrgIds === 'function' ? getAdminOrgIds() : [];
  const orgNavHref = adminOrgIds.length === 1
    ? `/organization/${adminOrgIds[0]}/admin`
    : '/admin/organizations';
  const orgNavLabel = adminOrgIds.length > 1 ? 'Organizations' : 'Organization';
  const hasCourses = courses && courses.length > 0;
  const toolsFirst = isToolsFirst(preferences, hasCourses);

  // Debug: log sidebar mode
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[AppLayout] toolsFirst:', toolsFirst, 'hasCourses:', hasCourses, 'has_courses pref:', preferences?.has_courses);
  }

  // Build primary nav items — order depends on whether user has courses
  const navItems = useMemo(() => {
    const dashboard = { key: 'dashboard', ariaLabel: 'Dashboard', hoverLabel: 'Dashboard', hoverDescription: 'Your academic command center', icon: <DashboardIcon />, iconActive: <DashboardIconSelected />, href: '/dashboard', active: currentPath.startsWith('/dashboard') };
    const aiTools = { key: 'ai-tools', ariaLabel: 'AI Tools', hoverLabel: "Scout's Desk", hoverDescription: 'Chat, quizzes, flashcards & more', icon: <ChatIcon />, iconActive: <ChatIconSelected />, href: '/ai-tools', active: currentPath.startsWith('/ai-tools') || currentPath.startsWith('/chat') };
    const calendar = { key: 'calendar', ariaLabel: 'Calendar', hoverLabel: 'Calendar', hoverDescription: 'Classes, exams & deadlines', icon: <CalendarIcon />, iconActive: <CalendarIconSelected />, href: '/calendar', active: currentPath.startsWith('/calendar') };
    const documents = { key: 'documents', ariaLabel: 'Documents', hoverLabel: 'Documents', hoverDescription: 'Notes, syllabi & uploads', icon: <DocumentsIcon />, iconActive: <DocumentsIconSelected />, href: '/documents', active: currentPath.startsWith('/documents') };

    if (toolsFirst) {
      return [aiTools, dashboard, calendar, documents];
    }
    return [dashboard, aiTools, calendar, documents];
  }, [toolsFirst, currentPath]);

  return (
    <>
      <ThemeLoader />
      <AppShell
        sidebar={accessToken ? (
          <Sidebar>
            <SidebarSection>
              {navItems.map(item => (
                <SidebarItem key={item.key} ariaLabel={item.ariaLabel} hoverLabel={item.hoverLabel} hoverDescription={item.hoverDescription} icon={item.icon} iconActive={item.iconActive} href={item.href} active={item.active} />
              ))}
              {STUDY_GUIDE_ENABLED && <SidebarItem ariaLabel="My Progress" hoverLabel="My Progress" hoverDescription="Track your study streaks" icon={<ProgressIcon />} iconActive={<ProgressIconSelected />} href="/my-progress" active={currentPath.startsWith('/my-progress')} />}
            </SidebarSection>
            <SidebarSection>
              <SidebarItem ariaLabel="Account" hoverLabel="Account" hoverDescription="Profile & preferences" icon={<PersonIcon />} href="/profile" active={currentPath.startsWith('/profile')} />
            </SidebarSection>
            {showAdminNav && (
              <SidebarSection>
                <SidebarItem
                  ariaLabel="My Classes"
                  hoverLabel="My Classes"
                  hoverDescription="Courses you manage"
                  icon={<ClassesAdminIcon />}
                  href="/my-classes"
                  active={currentPath.startsWith('/my-classes') || /^\/course\/\d+\/admin/.test(currentPath)}
                />
                {(isAdmin || adminOrgIds.length > 0) && (
                  <SidebarItem
                    ariaLabel={orgNavLabel}
                    hoverLabel={orgNavLabel}
                    hoverDescription="Org members, courses & domains"
                    icon={<OrgAdminIcon />}
                    href={orgNavHref}
                    active={currentPath.startsWith('/organization/') || currentPath.startsWith('/admin/organizations')}
                  />
                )}
              </SidebarSection>
            )}
            {isAdmin && (
              <SidebarSection>
                <SidebarItem ariaLabel="Settings" hoverLabel="Settings" hoverDescription="Admin controls" icon={<SettingsIcon />} href="/settings" active={currentPath.startsWith('/settings')} />
              </SidebarSection>
            )}
          </Sidebar>
        ) : null}
      >
        <Outlet />
      </AppShell>
    </>
  );
}

export default AppLayout;
